// LUMINA · pages/vajra/admin/ContactsSection.jsx
// Phase 121: 订阅者 — Google CSV 导入 → 浏览/搜索/标签筛选/推送至群组
// Phase 121B: 内联标签编辑、姓名编辑、删除确认增强

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { API, GD, DG, CR, TXT, PANEL, SUBSCRIBER_LABELS } from '../constants';

export default function ContactsSection({ token, headers }) {
  // ── State ──────────────────────────────────────────────────────────────────
  const [contacts,       setContacts]       = useState([]);
  const [stats,          setStats]          = useState(null);
  const [loading,        setLoading]        = useState(false);
  const [importing,      setImporting]      = useState(false);
  const [importMsg,      setImportMsg]      = useState("");
  const [pushMsg,        setPushMsg]        = useState("");

  // search / filter / sort / pagination
  const [query,          setQuery]          = useState("");
  const [activeLabel,    setActiveLabel]    = useState("");
  const [sortBy,         setSortBy]         = useState("imported_at");
  const [page,           setPage]           = useState(1);
  const perPage = 50;

  // checkbox state
  const [selectedIds,    setSelectedIds]    = useState(new Set());
  const [selectAll,      setSelectAll]      = useState(false);

  // push-to-group
  const [emailGroups,    setEmailGroups]    = useState([]);
  const [pushTargetGroup, setPushTargetGroup] = useState("");
  const [pushing,        setPushing]        = useState(false);

  // ── Inline editing state ───────────────────────────────────────────────────
  const [editingId,      setEditingId]      = useState(null);   // subscriber id being edited
  const [editName,       setEditName]       = useState("");
  const [editLabels,     setEditLabels]     = useState(new Set());
  const [saving,         setSaving]         = useState(false);
  const [editMsg,        setEditMsg]        = useState("");

  // ── Delete state ───────────────────────────────────────────────────────────
  const [deletingId,     setDeletingId]     = useState(null);   // awaiting confirm
  const [deleting,       setDeleting]       = useState(false);

  const searchTimeout = useRef(null);
  const fileInputRef = useRef(null);

  // ── Loaders ────────────────────────────────────────────────────────────────
  const loadStats = useCallback(async () => {
    try {
      const r = await fetch(`${API}/vajra/admin/subscribers/stats`, { headers });
      if (r.ok) setStats(await r.json());
    } catch (e) { /* ignore */ }
  }, [token]);

  const loadContacts = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: String(page), per_page: String(perPage), sort_by: sortBy, order: "desc" });
      if (query.trim()) params.set("q", query.trim());
      if (activeLabel) params.set("label", activeLabel);
      const r = await fetch(`${API}/vajra/admin/subscribers?${params}`, { headers });
      if (r.ok) {
        const d = await r.json();
        setContacts(d.contacts || []);
        setStats(s => ({ ...(s || {}), total_subscribers: d.total }));
      } else {
        console.warn("ContactsSection: API returned", r.status, await r.text().catch(() => ""));
      }
    } catch (e) { console.warn("ContactsSection: fetch error", e); }
    setLoading(false);
  }, [token, page, query, activeLabel, sortBy]);

  const loadEmailGroups = useCallback(async () => {
    try {
      const d = await fetch(`${API}/vajra/staff/email-groups`, { headers }).then(r => r.ok ? r.json() : []);
      setEmailGroups(Array.isArray(d) ? d : []);
    } catch (e) { /* ignore */ }
  }, [token]);

  useEffect(() => { loadStats(); loadEmailGroups(); }, [loadStats, loadEmailGroups]);
  useEffect(() => { loadContacts(); }, [loadContacts]);

  // ── Inline editing handlers ────────────────────────────────────────────────
  const startEdit = (contact) => {
    setEditingId(contact.id);
    setEditName(contact.name || "");
    setEditLabels(new Set(contact.labels || []));
    setEditMsg("");
    setDeletingId(null); // close delete confirm if open
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditName("");
    setEditLabels(new Set());
    setEditMsg("");
  };

  const toggleEditLabel = (labelKey) => {
    setEditLabels(prev => {
      const next = new Set(prev);
      if (next.has(labelKey)) next.delete(labelKey); else next.add(labelKey);
      return next;
    });
  };

  const saveEdit = async () => {
    if (!editingId) return;
    setSaving(true);
    setEditMsg("");
    try {
      const body = {
        name: editName,
        labels: [...editLabels],
      };
      const r = await fetch(`${API}/vajra/admin/subscribers/${editingId}`, {
        method: "PATCH", headers, body: JSON.stringify(body),
      });
      if (r.ok) {
        setEditMsg("✓ 已保存");
        cancelEdit();
        loadContacts();
        loadStats();
      } else {
        const d = await r.json().catch(() => ({}));
        setEditMsg(`✗ ${d.detail || "保存失败"}`);
      }
    } catch (e) {
      setEditMsg("✗ 网络错误");
    }
    setSaving(false);
  };

  // ── Delete handler ─────────────────────────────────────────────────────────
  const confirmDelete = (id) => {
    setDeletingId(id);
    setEditingId(null); // close edit if open
  };

  const cancelDelete = () => {
    setDeletingId(null);
  };

  const executeDelete = async (id) => {
    setDeleting(true);
    try {
      const r = await fetch(`${API}/vajra/admin/subscribers/${id}`, { method: "DELETE", headers });
      if (r.ok) {
        setDeletingId(null);
        loadContacts();
        loadStats();
      } else {
        const d = await r.json().catch(() => ({}));
        setEditMsg(`✗ 删除失败: ${d.detail || "未知错误"}`);
        setDeletingId(null);
      }
    } catch (e) {
      setEditMsg("✗ 网络错误");
      setDeletingId(null);
    }
    setDeleting(false);
  };

  // ── Search / filter / select handlers ──────────────────────────────────────
  const handleSearch = (v) => {
    setQuery(v);
    setPage(1);
    setSelectedIds(new Set());
    setSelectAll(false);
  };

  const handleLabelClick = (label) => {
    if (activeLabel === label) {
      setActiveLabel("");
    } else {
      setActiveLabel(label);
    }
    setPage(1);
    setSelectedIds(new Set());
    setSelectAll(false);
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
    setSelectAll(false);
  };

  const toggleSelectAll = () => {
    if (selectAll) {
      setSelectedIds(new Set());
      setSelectAll(false);
    } else {
      setSelectedIds(new Set(contacts.filter(c => c.email).map(c => c.id)));
      setSelectAll(true);
    }
  };

  const handleImportClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileSelected = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    setImportMsg("");
    try {
      const formData = new FormData();
      formData.append("csv_file", file);
      const r = await fetch(`${API}/vajra/admin/subscribers/upload-import`, {
        method: "POST", headers: { "Authorization": headers.Authorization }, body: formData,
      });
      const d = await r.json();
      if (r.ok) {
        setImportMsg(`✓ 导入 ${d.imported} 人，更新 ${d.updated} 人，跳过 ${d.skipped} 人（共 ${d.total} 人）`);
        loadStats();
        loadContacts();
      } else {
        setImportMsg(`✗ ${d.detail || "导入失败"}`);
      }
    } catch (e) {
      setImportMsg("✗ 网络错误");
    }
    setImporting(false);
    e.target.value = "";
  };

  const handlePush = async () => {
    if (!pushTargetGroup) return;
    setPushing(true);
    setPushMsg("");
    try {
      const body = { group_id: parseInt(pushTargetGroup) };
      if (selectedIds.size > 0) {
        body.subscriber_ids = [...selectedIds];
      } else if (activeLabel) {
        body.label = activeLabel;
      } else {
        setPushMsg("✗ 请先选择一个标签或勾选联系人");
        setPushing(false);
        return;
      }
      const r = await fetch(`${API}/vajra/admin/subscribers/push-to-group`, {
        method: "POST", headers, body: JSON.stringify(body),
      });
      const d = await r.json();
      if (r.ok) {
        setPushMsg(`✓ 已推送 ${d.pushed} 人（${d.already_in_group} 人已在群组中）`);
        setSelectedIds(new Set());
        setSelectAll(false);
      } else {
        setPushMsg(`✗ ${d.detail || "推送失败"}`);
      }
    } catch (e) {
      setPushMsg("✗ 网络错误");
    }
    setPushing(false);
  };

  // ── Render helpers ─────────────────────────────────────────────────────────
  const labelChip = (label, display) => (
    <span key={label} title={label}
      style={{
        display: "inline-block", padding: "1px 6px", margin: "1px 2px",
        background: activeLabel === label ? "rgba(212,175,55,0.15)" : "rgba(0,0,0,0.04)",
        color: activeLabel === label ? GD : "#666",
        borderRadius: 4, fontSize: 10, cursor: "pointer", whiteSpace: "nowrap",
        border: activeLabel === label ? `1px solid ${GD}` : "1px solid transparent",
      }}
      onClick={(e) => { e.stopPropagation(); handleLabelClick(label); }}>
      {display || label}
    </span>
  );

  // ── Available labels sorted by display name ────────────────────────────────
  const availableLabels = Object.entries(SUBSCRIBER_LABELS).sort((a, b) => a[1].localeCompare(b[1]));

  const distribution = stats?.label_distribution || [];
  const selectedCount = selectAll ? contacts.length : selectedIds.size;

  return (
    <div>
      {/* ── Stats bar ────────────────────────────────────────────────────── */}
      <div style={{ background: PANEL, borderRadius: 12, padding: "20px", border: "1px solid rgba(0,0,0,0.06)", marginBottom: 20 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 16 }}>
          <div>
            <div style={{ fontSize: 15, color: GD, fontWeight: 600, marginBottom: 4 }}>📇 订阅者</div>
            <div style={{ fontSize: 11, color: "#aaa" }}>
              {stats ? `共 ${stats.total_subscribers} 人 · 最后导入 ${stats.last_imported_at ? stats.last_imported_at.slice(0, 10) : "—"}` : "加载中…"}
            </div>
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <button onClick={handleImportClick} disabled={importing}
              style={{
                padding: "8px 16px", background: importing ? "#aaa" : GD, color: "#fff",
                border: "none", borderRadius: 8, fontSize: 13, cursor: importing ? "not-allowed" : "pointer",
              }}>
              {importing ? "⏳ 导入中…" : "📁 上传 CSV"}
            </button>
            <input type="file" ref={fileInputRef} onChange={handleFileSelected}
              accept=".csv" style={{ display: "none" }} />
            {emailGroups.length > 0 && (
              <>
                <select value={pushTargetGroup} onChange={e => setPushTargetGroup(e.target.value)}
                  style={{ padding: "8px 12px", border: `1px solid ${GD}`, borderRadius: 8, fontSize: 12, background: "#fff", color: GD }}>
                  <option value="">推送到群组▾</option>
                  {emailGroups.map(g => <option key={g.id} value={g.id}>{g.name}（{g.member_count}人）</option>)}
                </select>
                <button onClick={handlePush} disabled={pushing || !pushTargetGroup}
                  style={{
                    padding: "8px 16px", background: pushing ? "#aaa" : CR, color: "#fff",
                    border: "none", borderRadius: 8, fontSize: 13, cursor: pushing || !pushTargetGroup ? "not-allowed" : "pointer",
                  }}>
                  {pushing ? "推送中…" : `推送${selectedCount > 0 ? ` (${selectedCount})` : ''}`}
                </button>
              </>
            )}
          </div>
        </div>
        {importMsg && (
          <div style={{ marginTop: 10, fontSize: 12, padding: "6px 12px", borderRadius: 6,
            background: importMsg.startsWith("✓") ? "rgba(6,78,59,0.06)" : "rgba(107,26,26,0.06)",
            color: importMsg.startsWith("✓") ? DG : CR }}>{importMsg}</div>
        )}
        {pushMsg && (
          <div style={{ marginTop: 10, fontSize: 12, padding: "6px 12px", borderRadius: 6,
            background: pushMsg.startsWith("✓") ? "rgba(6,78,59,0.06)" : "rgba(107,26,26,0.06)",
            color: pushMsg.startsWith("✓") ? DG : CR }}>{pushMsg}</div>
        )}
      </div>

      <div style={{ display: "flex", gap: 20, alignItems: "flex-start", flexWrap: "wrap" }}>
        {/* ── Left: label sidebar ────────────────────────────────────────── */}
        <div style={{ flex: "0 0 220px", minWidth: 180 }}>
          <div style={{
            background: PANEL, borderRadius: 12, padding: "16px",
            border: "1px solid rgba(0,0,0,0.06)",
          }}>
            <div style={{ fontSize: 12, color: GD, fontWeight: 600, marginBottom: 10 }}>标签分布</div>
            <div
              onClick={() => handleLabelClick("")}
              style={{
                padding: "6px 10px", borderRadius: 6, marginBottom: 4, cursor: "pointer", fontSize: 12,
                background: !activeLabel ? "rgba(212,175,55,0.1)" : "transparent",
                color: !activeLabel ? GD : TXT, fontWeight: !activeLabel ? 600 : 400,
              }}>
              全部标签
            </div>
            {distribution.map(d => (
              <div key={d.label}
                onClick={() => handleLabelClick(d.label)}
                style={{
                  padding: "6px 10px", borderRadius: 6, marginBottom: 2, cursor: "pointer", fontSize: 11,
                  display: "flex", justifyContent: "space-between",
                  background: activeLabel === d.label ? "rgba(212,175,55,0.1)" : "transparent",
                  color: activeLabel === d.label ? GD : TXT,
                  fontWeight: activeLabel === d.label ? 600 : 400,
                }}>
                <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1 }}>
                  {d.display_label || d.label}
                </span>
                <span style={{ color: "#aaa", marginLeft: 8, flexShrink: 0 }}>{d.count}</span>
              </div>
            ))}
          </div>
        </div>

        {/* ── Right: table ───────────────────────────────────────────────── */}
        <div style={{ flex: 1, minWidth: 400 }}>
          <div style={{
            background: PANEL, borderRadius: 12,
            border: "1px solid rgba(0,0,0,0.06)", overflow: "hidden",
          }}>
            {/* Search bar */}
            <div style={{ padding: "12px 16px", display: "flex", gap: 12, alignItems: "center", borderBottom: "1px solid rgba(0,0,0,0.06)" }}>
              <input
                value={query}
                onChange={e => { clearTimeout(searchTimeout.current); searchTimeout.current = setTimeout(() => handleSearch(e.target.value), 300); }}
                placeholder="🔍 搜索姓名或邮箱…"
                style={{
                  flex: 1, padding: "8px 12px", border: "1px solid rgba(0,0,0,0.1)", borderRadius: 8,
                  fontSize: 13, boxSizing: "border-box",
                }} />
              <select value={sortBy} onChange={e => { setSortBy(e.target.value); setPage(1); }}
                style={{ padding: "8px 10px", border: "1px solid rgba(0,0,0,0.1)", borderRadius: 8, fontSize: 12, background: "#fff" }}>
                <option value="imported_at">导入时间</option>
                <option value="name">姓名</option>
                <option value="email">邮箱</option>
              </select>
            </div>

            {/* Global edit status message */}
            {editMsg && !editingId && (
              <div style={{ margin: "8px 16px", fontSize: 12, padding: "6px 12px", borderRadius: 6,
                background: editMsg.startsWith("✓") ? "rgba(6,78,59,0.06)" : "rgba(107,26,26,0.06)",
                color: editMsg.startsWith("✓") ? DG : CR }}>
                {editMsg}
              </div>
            )}

            {/* Table */}
            {loading ? (
              <div style={{ padding: "40px", textAlign: "center", color: "#ccc", fontSize: 13 }}>加载中…</div>
            ) : contacts.length === 0 ? (
              <div style={{ padding: "40px", textAlign: "center", color: "#ccc", fontSize: 13 }}>
                暂无数据 · 点击"上传 CSV"导入订阅者
              </div>
            ) : (
              <>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                  <thead>
                    <tr style={{ borderBottom: "1px solid rgba(0,0,0,0.08)", background: "rgba(0,0,0,0.02)" }}>
                      <th style={{ padding: "6px 10px", width: 30 }}>
                        <input type="checkbox" checked={selectAll} onChange={toggleSelectAll}
                          style={{ cursor: "pointer" }} />
                      </th>
                      <th style={{ padding: "6px 10px", textAlign: "left", color: "#aaa", fontWeight: 400, fontSize: 11 }}>姓名</th>
                      <th style={{ padding: "6px 10px", textAlign: "left", color: "#aaa", fontWeight: 400, fontSize: 11 }}>邮箱</th>
                      <th style={{ padding: "6px 10px", textAlign: "left", color: "#aaa", fontWeight: 400, fontSize: 11 }}>标签</th>
                      <th style={{ padding: "6px 10px", textAlign: "left", color: "#aaa", fontWeight: 400, fontSize: 11 }}>备注</th>
                      <th style={{ padding: "6px 10px", textAlign: "right", color: "#aaa", fontWeight: 400, fontSize: 11, width: 60 }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {contacts.map(c => (
                      <React.Fragment key={c.id}>
                        {/* ── Main row ──────────────────────────────────── */}
                        <tr style={{
                          borderBottom: editingId === c.id || deletingId === c.id
                            ? "none" : "1px solid rgba(0,0,0,0.04)",
                          background: editingId === c.id ? "rgba(212,175,55,0.04)" : "transparent",
                        }}>
                          <td style={{ padding: "6px 10px", textAlign: "center" }}>
                            <input type="checkbox"
                              checked={selectAll || selectedIds.has(c.id)}
                              onChange={() => toggleSelect(c.id)}
                              style={{ cursor: "pointer" }} />
                          </td>
                          <td style={{ padding: "6px 10px", color: TXT, maxWidth: 120, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {c.name || "—"}
                          </td>
                          <td style={{ padding: "6px 10px", color: "#666", fontSize: 11, maxWidth: 180, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {c.email}
                          </td>
                          <td style={{ padding: "6px 10px", maxWidth: 200 }}>
                            <div style={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                              {(c.display_labels || c.labels || []).map((dl, i) => labelChip(c.labels[i], dl))}
                            </div>
                          </td>
                          <td style={{ padding: "6px 10px", color: "#999", fontSize: 11, maxWidth: 120, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {c.notes || "—"}
                          </td>
                          <td style={{ padding: "6px 10px", textAlign: "right", whiteSpace: "nowrap" }}>
                            <button onClick={() => startEdit(c)}
                              style={{ background: "none", border: "none", color: "#bbb", cursor: "pointer", fontSize: 13, marginRight: 4 }}
                              title="编辑">✏️</button>
                            <button onClick={() => confirmDelete(c.id)}
                              style={{ background: "none", border: "none", color: "#ddd", cursor: "pointer", fontSize: 14 }}
                              title="删除">×</button>
                          </td>
                        </tr>

                        {/* ── Inline editing panel ──────────────────────── */}
                        {editingId === c.id && (
                          <tr>
                            <td colSpan={6} style={{ padding: 0 }}>
                              <div style={{
                                padding: "16px 20px",
                                borderBottom: "1px solid rgba(0,0,0,0.04)",
                                background: "rgba(212,175,55,0.03)",
                              }}>
                                {/* Name field */}
                                <div style={{ marginBottom: 12 }}>
                                  <div style={{ fontSize: 11, color: "#888", marginBottom: 4 }}>姓名</div>
                                  <input
                                    value={editName}
                                    onChange={e => setEditName(e.target.value)}
                                    placeholder="输入姓名"
                                    style={{
                                      padding: "6px 10px", border: "1px solid rgba(0,0,0,0.12)", borderRadius: 6,
                                      fontSize: 13, width: "100%", maxWidth: 300, boxSizing: "border-box",
                                    }} />
                                </div>

                                {/* Label selector */}
                                <div style={{ marginBottom: 12 }}>
                                  <div style={{ fontSize: 11, color: "#888", marginBottom: 6 }}>标签（点击切换）</div>
                                  <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                                    {availableLabels.map(([key, display]) => {
                                      const isActive = editLabels.has(key);
                                      return (
                                        <span key={key}
                                          onClick={() => toggleEditLabel(key)}
                                          style={{
                                            display: "inline-block", padding: "3px 10px",
                                            borderRadius: 14, fontSize: 11, cursor: "pointer",
                                            whiteSpace: "nowrap", userSelect: "none",
                                            background: isActive ? GD : "rgba(0,0,0,0.04)",
                                            color: isActive ? "#fff" : "#666",
                                            border: isActive ? `1px solid ${GD}` : "1px solid rgba(0,0,0,0.1)",
                                            transition: "all 0.15s",
                                          }}>
                                          {display}
                                        </span>
                                      );
                                    })}
                                  </div>
                                </div>

                                {/* Action buttons */}
                                <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                                  <button onClick={saveEdit} disabled={saving}
                                    style={{
                                      padding: "6px 16px", background: saving ? "#aaa" : DG, color: "#fff",
                                      border: "none", borderRadius: 6, fontSize: 12,
                                      cursor: saving ? "not-allowed" : "pointer",
                                    }}>
                                    {saving ? "保存中…" : "✓ 保存"}
                                  </button>
                                  <button onClick={cancelEdit}
                                    style={{
                                      padding: "6px 16px", background: "rgba(0,0,0,0.04)", color: "#666",
                                      border: "1px solid rgba(0,0,0,0.1)", borderRadius: 6, fontSize: 12,
                                      cursor: "pointer",
                                    }}>
                                    取消
                                  </button>
                                  {editMsg && (
                                    <span style={{
                                      fontSize: 11, marginLeft: 8, padding: "2px 8px", borderRadius: 4,
                                      background: editMsg.startsWith("✓") ? "rgba(6,78,59,0.06)" : "rgba(107,26,26,0.06)",
                                      color: editMsg.startsWith("✓") ? DG : CR,
                                    }}>{editMsg}</span>
                                  )}
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}

                        {/* ── Delete confirmation panel ─────────────────── */}
                        {deletingId === c.id && (
                          <tr>
                            <td colSpan={6} style={{ padding: 0 }}>
                              <div style={{
                                padding: "12px 20px",
                                borderBottom: "1px solid rgba(0,0,0,0.04)",
                                background: "rgba(107,26,26,0.03)",
                                display: "flex", alignItems: "center", gap: 12, fontSize: 12,
                              }}>
                                <span style={{ color: CR }}>确认删除 <strong>{c.name || c.email}</strong>？</span>
                                <button onClick={() => executeDelete(c.id)} disabled={deleting}
                                  style={{
                                    padding: "4px 12px", background: deleting ? "#aaa" : CR, color: "#fff",
                                    border: "none", borderRadius: 4, fontSize: 11,
                                    cursor: deleting ? "not-allowed" : "pointer",
                                  }}>
                                  {deleting ? "删除中…" : "确认删除"}
                                </button>
                                <button onClick={cancelDelete}
                                  style={{
                                    padding: "4px 12px", background: "rgba(0,0,0,0.04)", color: "#666",
                                    border: "1px solid rgba(0,0,0,0.1)", borderRadius: 4, fontSize: 11,
                                    cursor: "pointer",
                                  }}>
                                  取消
                                </button>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>

                {/* Pagination */}
                <div style={{
                  padding: "10px 16px", borderTop: "1px solid rgba(0,0,0,0.06)",
                  display: "flex", justifyContent: "center", alignItems: "center", gap: 12, fontSize: 12,
                }}>
                  <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1}
                    style={{ padding: "4px 10px", border: "1px solid rgba(0,0,0,0.1)", borderRadius: 6, background: "#fff", cursor: page <= 1 ? "default" : "pointer", color: page <= 1 ? "#ccc" : TXT }}>
                    ← 上一页
                  </button>
                  <span style={{ color: "#888" }}>
                    第 {page} / {stats ? Math.ceil(stats.total_subscribers / perPage) : "?"} 页
                  </span>
                  <button onClick={() => setPage(p => p + 1)}
                    style={{ padding: "4px 10px", border: "1px solid rgba(0,0,0,0.1)", borderRadius: 6, background: "#fff", cursor: "pointer", color: TXT }}>
                    下一页 →
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
